import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppLayout } from '@/layouts/app-layout'
import { HomeRedirect, ProtectedRoute } from '@/components/shared/guards'
import { LoginPage } from '@/pages/auth/login-page'
import { RegisterPage } from '@/pages/auth/register-page'
import { ForgotPasswordPage } from '@/pages/auth/forgot-password-page'
import { LegalPage } from '@/pages/auth/legal-page'
import { AuthShell } from '@/pages/auth/auth-layout'
import { DashboardPage } from '@/pages/dashboard/dashboard-page'
import { OnboardingPage } from '@/pages/onboarding/onboarding-page'

// Hội thoại
import { ConversationsPage } from '@/pages/conversations/conversations-page'
import { QuickRepliesRoute } from '@/pages/conversations/quick-replies-route'
// Khách hàng
import { CustomersPage } from '@/pages/customers/customers-page'
import { ContactDetailPage } from '@/pages/customers/contact-detail-page'
import { DuplicateContactsPage } from '@/pages/customers/duplicate-contacts-page'
import { PhoneExtractPage } from '@/pages/customers/phone-extract-page'
// Phân tích
import { AnalyticsPage } from '@/pages/analytics/analytics-page'
import { SavedReportsPage } from '@/pages/analytics/saved-reports-page'
// AI
import { AiIndexRedirect } from '@/pages/ai/ai-index-redirect'
import { AiStartPage } from '@/pages/ai/ai-start-page'
import { AiBotsPage } from '@/pages/ai/ai-bots-page'
import { NewBotWizardPage } from '@/pages/ai/wizard/new-bot-wizard-page'
import { AiLibraryPage } from '@/pages/ai/ai-library-page'
import { AiTrackingPage } from '@/pages/ai/ai-tracking-page'
import { AiSourcesPage } from '@/pages/ai/ai-sources-page'
import { AiStaffPage } from '@/pages/ai/staff/staff-page'
import { LegacyTrainRedirect } from '@/pages/ai/staff/legacy-train-redirect'
import { TrainAnalyticsPage } from '@/pages/ai/train/train-analytics-page'
import { KnowledgeBasesPage } from '@/pages/ai/knowledge/knowledge-bases-page'
import { KnowledgeBaseDetailPage } from '@/pages/ai/knowledge/knowledge-base-detail-page'
// Cài đặt
import { SettingsPage } from '@/pages/settings/settings-page'
// Cài đặt cá nhân
import { AccountPage } from '@/pages/account/account-page'
// Tự động hóa
import { AutomationPage } from '@/pages/automation/automation-page'
import { AutomationFlowPage } from '@/pages/automation/automation-flow-page'
// Sản phẩm (nguồn dữ liệu cho nhân viên AI)
import { ProductsPage } from '@/pages/products/products-page'
// ZNS
import { ZnsCampaignsPage } from '@/pages/zns/zns-campaigns-page'
import { ZnsCampaignDetailPage } from '@/pages/zns/zns-campaign-detail-page'
import { FriendCampaignsPage } from '@/pages/zalo-friends/friend-campaigns-page'
import { FriendCampaignDetailPage } from '@/pages/zalo-friends/friend-campaign-detail-page'
import { BroadcastCampaignsPage } from '@/pages/zalo-broadcast/broadcast-campaigns-page'
import { BroadcastCampaignDetailPage } from '@/pages/zalo-broadcast/broadcast-campaign-detail-page'
// Báo giá
import { CdpRedirect } from '@/pages/cdp/cdp-redirect'
import { SegmentsPage } from '@/pages/customers/segments-page'
// Tích hợp
import { IntegrationsLayout } from '@/pages/integrations/integrations-layout'
import { TeamMembersPage } from '@/pages/team/team-members-page'
import { ChannelsOverviewPage } from '@/pages/integrations/channels-overview-page'
import { ChannelDetailPage } from '@/pages/integrations/channel-detail-page'
import { AppsPage } from '@/pages/integrations/apps-page'
import { AppDetailPage } from '@/pages/integrations/app-detail-page'
// Gói dịch vụ
import { BillingPage } from '@/pages/billing/billing-page'
import { UpgradePage } from '@/pages/billing/upgrade-page'
import { AddonPage } from '@/pages/billing/addon-page'
import { PaymentResultPage } from '@/pages/billing/payment-result-page'
// Platform (super-admin) — auth & layout riêng
import { PlatformLayout } from '@/layouts/platform-layout'
import { PlatformLoginPage } from '@/pages/platform/platform-login-page'
import { PlatformDashboardPage } from '@/pages/platform/platform-dashboard-page'
import { OrgsPage } from '@/pages/platform/orgs-page'
import { OrgDetailPage } from '@/pages/platform/org-detail-page'
import { PlatformBrandingPage } from '@/pages/platform/platform-branding-page'
import { PlatformReleaseNotesPage } from '@/pages/platform/platform-release-notes-page'
import { PlatformLandingPage } from '@/pages/platform/platform-landing-page'
import { LandingPage } from '@/pages/public/landing-page'
import { PlatformAuditPage } from '@/pages/platform/platform-audit-page'
import { PlatformChannelsPage } from '@/pages/platform/platform-channels-page'
import { PlatformAiPage } from '@/pages/platform/platform-ai-page'
import { PlatformAiKeysPage } from '@/pages/platform/ai/platform-ai-keys-page'
import { PlatformAiCatalogPage } from '@/pages/platform/ai/platform-ai-catalog-page'
import { PlatformAiOrgsPage } from '@/pages/platform/ai/platform-ai-orgs-page'
import { PlatformAiSettingsPage } from '@/pages/platform/ai/platform-ai-settings-page'
import { PlatformAdminsPage } from '@/pages/platform/platform-admins-page'
import { PlatformPricingPage } from '@/pages/platform/platform-pricing-page'
import { PlatformInvoicesPage } from '@/pages/platform/platform-invoices-page'
import { PlatformPromoPage } from '@/pages/platform/platform-promo-page'
import { PlatformFeedbackPage } from '@/pages/platform/platform-feedback-page'
import { PlatformBillingLarkPage } from '@/pages/platform/platform-billing-lark-page'
import { ManualQuotesPage } from '@/pages/platform/manual-quote/manual-quotes-page'
import { ManualQuoteEditorPage } from '@/pages/platform/manual-quote/manual-quote-editor-page'
import { ManualQuotePrintPage } from '@/pages/platform/manual-quote/manual-quote-print-page'
import { PlatformAccountPage } from '@/pages/platform/platform-account-page'
// Tiếp thị liên kết (affiliate)
import { AffiliatePage } from '@/pages/affiliate/affiliate-page'
import { ReferralLandingPage } from '@/pages/affiliate/referral-landing-page'
import { AffiliateOverviewPage } from '@/pages/platform/affiliate/affiliate-overview-page'
import { AffiliatePartnersPage } from '@/pages/platform/affiliate/affiliate-partners-page'
import { AffiliateReferralsPage } from '@/pages/platform/affiliate/affiliate-referrals-page'
import { AffiliateCommissionsPage } from '@/pages/platform/affiliate/affiliate-commissions-page'
import { AffiliateWithdrawalsPage } from '@/pages/platform/affiliate/affiliate-withdrawals-page'
import { AffiliateSettingsPage } from '@/pages/platform/affiliate/affiliate-settings-page'
import { AffiliateTaxPage } from '@/pages/platform/affiliate/affiliate-tax-page'
// Kết nối AI (Claude) — màn đồng ý OAuth
import { ConsentPage } from '@/pages/oauth/consent-page'

export const router = createBrowserRouter([
  // Đăng nhập / đăng ký / quên mật khẩu: hộp nổi trên nền giả lập hệ thống (AuthShell giữ nguyên nền khi đổi trang
  // con). Công khai; đã đăng nhập → về trang đầu.
  {
    element: <AuthShell />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/signup', element: <RegisterPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  // Trang giới thiệu eCDP (công khai) — tên miền ecdp.vn mở thẳng ở "/" (main.tsx).
  { path: '/gioi-thieu', element: <LandingPage /> },
  { path: '/dieu-khoan', element: <LegalPage kind="terms" /> },
  { path: '/chinh-sach-bao-mat', element: <LegalPage kind="privacy" /> },
  { path: '/ket-noi-claude', element: <LegalPage kind="claude-connector" /> },
  // Hướng dẫn xoá dữ liệu — URL khai với Meta (User Data Deletion); /data-deletion là bí danh tiếng Anh.
  { path: '/xoa-du-lieu', element: <LegalPage kind="data-deletion" /> },
  { path: '/data-deletion', element: <LegalPage kind="data-deletion" /> },
  // Trang báo giá công khai (không cần đăng nhập, ngoài AppLayout)
  // Link giới thiệu (Tiếp thị liên kết) — công khai, không chuyển hướng ra ngoài
  { path: '/r/:code', element: <ReferralLandingPage /> },

  // Khu vực Platform super-admin (auth + layout riêng, guard nằm trong PlatformLayout)
  { path: '/platform/login', element: <PlatformLoginPage /> },
  // Bản in A4 báo giá thủ công — ngoài PlatformLayout để in trọn trang (trang tự kiểm tra đăng nhập platform)
  { path: '/platform/manual-quotes/:id/print', element: <ManualQuotePrintPage /> },
  {
    path: '/platform',
    element: <PlatformLayout />,
    children: [
      { index: true, element: <PlatformDashboardPage /> },
      { path: 'companies', element: <OrgsPage /> },
      { path: 'companies/:orgId', element: <OrgDetailPage /> },
      { path: 'pricing', element: <PlatformPricingPage /> },
      { path: 'promo-codes', element: <PlatformPromoPage /> },
      { path: 'invoices', element: <PlatformInvoicesPage /> },
      { path: 'feedback', element: <PlatformFeedbackPage /> },
      { path: 'lark', element: <PlatformBillingLarkPage /> },
      { path: 'branding', element: <PlatformBrandingPage /> },
      { path: 'release-notes', element: <PlatformReleaseNotesPage /> },
      { path: 'landing', element: <PlatformLandingPage /> },
      { path: 'audit', element: <PlatformAuditPage /> },
      { path: 'channels', element: <PlatformChannelsPage /> },
      { path: 'ai', element: <PlatformAiPage /> },
      { path: 'ai/keys', element: <PlatformAiKeysPage /> },
      { path: 'ai/catalog', element: <PlatformAiCatalogPage /> },
      { path: 'ai/orgs', element: <PlatformAiOrgsPage /> },
      { path: 'ai/credit-settings', element: <PlatformAiSettingsPage /> },
      { path: 'admins', element: <PlatformAdminsPage /> },
      { path: 'account', element: <PlatformAccountPage /> },
      { path: 'settings', element: <Navigate to="/platform/branding" replace /> },
      { path: 'affiliate', element: <AffiliateOverviewPage /> },
      { path: 'affiliate/partners', element: <AffiliatePartnersPage /> },
      { path: 'affiliate/referrals', element: <AffiliateReferralsPage /> },
      { path: 'affiliate/commissions', element: <AffiliateCommissionsPage /> },
      { path: 'affiliate/withdrawals', element: <AffiliateWithdrawalsPage /> },
      { path: 'affiliate/settings', element: <AffiliateSettingsPage /> },
      { path: 'affiliate/tax', element: <AffiliateTaxPage /> },
      { path: 'manual-quotes', element: <ManualQuotesPage /> },
      { path: 'manual-quotes/:id', element: <ManualQuoteEditorPage /> },
    ],
  },
  // Màn đồng ý OAuth của Kết nối AI (Claude): cần đăng nhập (chưa đăng nhập → /login rồi quay lại kèm `?request=`),
  // nhưng là hộp giữa màn, ngoài AppLayout.
  { path: '/oauth/consent', element: <ProtectedRoute><ConsentPage /></ProtectedRoute> },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      // Trang đầu theo quyền: có quyền chat → Hộp thư, không thì trang đầu tiên được phép.
      { index: true, element: <HomeRedirect /> },
      { path: 'dashboard', element: <DashboardPage /> },
      // Trang "Bắt đầu" — lần đầu đăng nhập của tài khoản mới (HomeRedirect), mở lại từ menu tài khoản.
      { path: 'onboarding', element: <OnboardingPage /> },

      { path: 'conversations', element: <ConversationsPage /> },
      { path: 'conversations/:id', element: <ConversationsPage /> },
      // Trả lời nhanh: đường dẫn riêng (phân quyền `quick_replies.view`) = màn Hội thoại + popup quản lý.
      { path: 'conversations/quick-replies', element: <QuickRepliesRoute /> },

      { path: 'customers', element: <CustomersPage /> },
      { path: 'customers/duplicates', element: <DuplicateContactsPage /> },
      { path: 'customers/extract-phones', element: <PhoneExtractPage /> },
      { path: 'customers/segments', element: <ProtectedRoute roles={['owner', 'admin', 'manager']}><SegmentsPage /></ProtectedRoute> },
      // Quyền thật là `contacts.import` (vai trò tuỳ chỉnh bật được) — trang tự kiểm qua /me/permissions.
      { path: 'customers/import', element: <Navigate to="/customers?import=1" replace /> },
      { path: 'customers/:id', element: <ContactDetailPage /> },

      { path: 'automation', element: <ProtectedRoute roles={['owner', 'admin', 'manager']}><AutomationPage /></ProtectedRoute> },
      { path: 'automation/flow/:ruleId', element: <ProtectedRoute roles={['owner', 'admin', 'manager']}><AutomationFlowPage /></ProtectedRoute> },

      { path: 'analytics', element: <ProtectedRoute roles={['owner', 'admin', 'manager']}><AnalyticsPage /></ProtectedRoute> },
      { path: 'analytics/saved-reports', element: <ProtectedRoute roles={['owner', 'admin', 'manager']}><SavedReportsPage /></ProtectedRoute> },

      { path: 'ai/san-pham', element: <ProductsPage /> },
      // Đường cũ (menu Bán hàng đã gỡ) — giữ cho dấu trang / link đã dán.
      { path: 'knowledge-base', element: <Navigate to="/ai/san-pham" replace /> },

      // Trang Phân khúc & CDP đã tách: phân khúc → tab của Khách hàng, phần còn lại → Cài đặt → Dữ liệu khách hàng.
      { path: 'cdp', element: <CdpRedirect /> },
      // Thư viện tài liệu bán hàng đã gộp vào Kho tri thức.
      { path: 'sales-docs', element: <Navigate to="/ai/knowledge" replace /> },
      { path: 'sales-docs/library', element: <Navigate to="/ai/knowledge" replace /> },

      // Nhân viên AI (menu cấp 1, khung ba tầng — docs/KE-HOACH-KHUNG-AI-3-TANG.md). `/ai` chỉ chuyển hướng:
      // link cũ ?tab=… → trang mới; còn lại → Nhân viên AI (hoặc Bắt đầu khi chưa có nhân viên AI nào đang trả lời).
      { path: 'ai', element: <AiIndexRedirect /> },
      { path: 'ai/bat-dau', element: <AiStartPage /> },
      { path: 'ai/nhan-vien', element: <AiBotsPage /> },
      // Luồng tạo nhân viên AI 5 bước (mẫu → kiến thức → chạy thử → kênh & giờ → ra mắt).
      { path: 'ai/nhan-vien/moi', element: <NewBotWizardPage /> },
      { path: 'ai/thu-vien', element: <AiLibraryPage /> },
      { path: 'ai/theo-doi', element: <AiTrackingPage /> },
      { path: 'ai/nguon', element: <ProtectedRoute roles={['owner', 'admin']}><AiSourcesPage /></ProtectedRoute> },
      // Màn một nhân viên AI (thay màn Dạy AI + form sửa) + trang Phân tích của nó.
      { path: 'ai/nhan-vien/:botId', element: <AiStaffPage /> },
      { path: 'ai/nhan-vien/:botId/phan-tich', element: <TrainAnalyticsPage /> },
      // Link cũ của màn Dạy AI → màn mới.
      { path: 'ai/train/:botId', element: <LegacyTrainRedirect to="detail" /> },
      { path: 'ai/train/:botId/handoff', element: <LegacyTrainRedirect to="handoff" /> },
      { path: 'ai/train/:botId/analytics', element: <LegacyTrainRedirect to="analytics" /> },
      // Link cũ: kịch bản → Thư viện (Kỹ năng); câu hỏi chưa trả lời được → Theo dõi.
      { path: 'ai/scenarios', element: <Navigate to="/ai/thu-vien" replace /> },
      { path: 'ai/knowledge-gaps', element: <Navigate to="/ai/theo-doi#cau-hoi" replace /> },
      // Kho tri thức độc lập: danh sách kho + một kho (/ai?tab=knowledge cũ chuyển hướng về đây).
      { path: 'ai/knowledge', element: <KnowledgeBasesPage /> },
      { path: 'ai/knowledge/:baseId', element: <KnowledgeBaseDetailPage /> },

      { path: 'zns-campaigns', element: <ZnsCampaignsPage /> },
      { path: 'zns-campaigns/:id', element: <ZnsCampaignDetailPage /> },
      { path: 'zalo-friend-campaigns', element: <FriendCampaignsPage /> },
      { path: 'zalo-friend-campaigns/:id', element: <FriendCampaignDetailPage /> },
      { path: 'zalo-broadcast-campaigns', element: <BroadcastCampaignsPage /> },
      { path: 'zalo-broadcast-campaigns/:id', element: <BroadcastCampaignDetailPage /> },

      {
        path: 'integrations',
        element: <IntegrationsLayout />,
        children: [
          { index: true, element: <ChannelsOverviewPage /> },
          { path: 'apps', element: <AppsPage /> },
          { path: 'apps/:appId', element: <AppDetailPage /> },
          { path: ':channel', element: <ChannelDetailPage /> },
        ],
      },

      { path: 'team/members', element: <ProtectedRoute roles={['owner', 'admin', 'manager']}><TeamMembersPage /></ProtectedRoute> },
      // "Phân quyền theo kênh" đã gộp vào Quản lý nhân viên (tab) — giữ đường dẫn cũ cho link/bookmark.
      { path: 'team/channel-access', element: <Navigate to="/team/members?view=channels" replace /> },

      { path: 'billing', element: <BillingPage /> },
      { path: 'billing/upgrade', element: <UpgradePage /> },
      { path: 'billing/addon', element: <AddonPage /> },
      { path: 'billing/result', element: <PaymentResultPage /> },
      { path: 'settings', element: <SettingsPage /> },
      // Hồ sơ cũ nằm trong Cài đặt → nay là Cài đặt cá nhân (tách khỏi cài đặt tổ chức).
      { path: 'settings/profile', element: <Navigate to="/account/profile" replace /> },
      { path: 'account', element: <AccountPage /> }, // điện thoại: danh sách mục; máy tính: chuyển về mục đầu
      { path: 'account/:section', element: <AccountPage /> },
      { path: 'affiliate', element: <AffiliatePage /> },
    ],
  },
  // Đường dẫn lạ → `/` (chọn trang đầu theo quyền; chưa đăng nhập thì sang /login).
  { path: '*', element: <Navigate to="/" replace /> },
])
